import {
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  // signInWithRedirect,
  getCurrentUser as amplifyGetCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  updateUserAttribute,
  confirmUserAttribute,
} from 'aws-amplify/auth';
import { ulid } from 'ulid';

export interface AuthUser {
  username: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  sub: string;
}

export interface SignUpResult {
  isSignUpComplete: boolean;
  userId?: string;
  username: string; // The generated ULID username needed for confirmation
  nextStep: {
    signUpStep: string;
    codeDeliveryDetails?: {
      deliveryMedium?: string;
      destination?: string;
    };
  };
}

// Get current authenticated user
export async function getAuthUser(): Promise<AuthUser | null> {
  try {
    const user = await amplifyGetCurrentUser();
    const attributes = await fetchUserAttributes();
    return {
      username: user.username,
      email: attributes.email,
      givenName: attributes.given_name,
      familyName: attributes.family_name,
      sub: user.userId,
    };
  } catch {
    return null;
  }
}

// Get ID token for API calls
export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

// Email/password sign in
export async function signIn(username: string, password: string): Promise<void> {
  await amplifySignIn({ username, password });
}

// Sign out
export function signOut(): void {
  amplifySignOut();
}

// Social login with redirect - COMMENTED OUT
// export function signInWithSocial(provider: 'Google' | 'Facebook'): void {
//   signInWithRedirect({ provider });
// }

// Open hosted UI (for generic login/signup) - COMMENTED OUT
// export function openHostedUI(): void {
//   signInWithRedirect({});
// }

// --- Sign-Up Functions ---

// Generate a unique username using ULID
function generateUsername(): string {
  return ulid();
}

// Register new user with email/password
export async function registerUser(
  email: string,
  password: string,
  givenName: string,
  familyName: string,
  locale?: string
): Promise<SignUpResult> {
  // Use a generated ULID as username since User Pool uses email as alias
  const username = generateUsername();

  // Use full name as display name
  const fullName = `${givenName} ${familyName}`.trim();

  const result = await signUp({
    username,
    password,
    options: {
      userAttributes: {
        email,
        name: fullName,
        given_name: givenName,
        family_name: familyName,
      },
      autoSignIn: true,
      clientMetadata: { locale: locale || 'en', appName: 'MyPinkContact' },
    },
  });

  // Extract codeDeliveryDetails if present (only on CONFIRM_SIGN_UP step)
  const codeDeliveryDetails =
    'codeDeliveryDetails' in result.nextStep
      ? result.nextStep.codeDeliveryDetails
      : undefined;

  return {
    isSignUpComplete: result.isSignUpComplete,
    userId: result.userId,
    username, // Return the ULID username for confirmation
    nextStep: {
      signUpStep: result.nextStep.signUpStep,
      codeDeliveryDetails,
    },
  };
}

// Confirm sign-up with verification code
export async function confirmRegistration(
  username: string,
  code: string
): Promise<boolean> {
  const result = await confirmSignUp({
    username,
    confirmationCode: code,
  });
  return result.isSignUpComplete;
}

// Resend verification code (uses email since Cognito maps it to username via alias)
export async function resendVerificationCode(
  email: string,
  locale?: string
): Promise<void> {
  await resendSignUpCode({
    username: email,
    options: { clientMetadata: { locale: locale || 'en', appName: 'MyPinkContact' } },
  });
}

// --- Password Reset Functions ---

// Initiate password reset (sends verification code to email)
export async function requestPasswordReset(
  email: string,
  locale?: string
): Promise<{ destination: string }> {
  const result = await resetPassword({
    username: email,
    options: { clientMetadata: { locale: locale || 'en', appName: 'MyPinkContact' } },
  });
  return {
    destination: result.nextStep.codeDeliveryDetails?.destination || email,
  };
}

// Complete password reset with verification code and new password
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
  locale?: string
): Promise<void> {
  await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
    options: { clientMetadata: { locale: locale || 'en', appName: 'MyPinkContact' } },
  });
}

// --- Authenticated User Account Functions ---

// Change password for authenticated user
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  await updatePassword({
    oldPassword,
    newPassword,
  });
}

// Request email change (sends verification code to new email)
export async function requestEmailChange(
  newEmail: string
): Promise<{ destination: string }> {
  const result = await updateUserAttribute({
    userAttribute: {
      attributeKey: 'email',
      value: newEmail,
    },
  });

  // Extract destination from codeDeliveryDetails if verification is needed
  const destination =
    result.nextStep.updateAttributeStep === 'CONFIRM_ATTRIBUTE_WITH_CODE' &&
    'codeDeliveryDetails' in result.nextStep
      ? result.nextStep.codeDeliveryDetails?.destination || newEmail
      : newEmail;

  return { destination };
}

// Confirm email change with verification code
export async function confirmEmailChange(code: string): Promise<void> {
  await confirmUserAttribute({
    userAttributeKey: 'email',
    confirmationCode: code,
  });
}
